import { useMemo } from 'react';
import { Proyeccion } from '../../../shared/types';
import { PatrimonioService } from '../services/patrimonio.service';
import { ProyeccionService } from '../services/proyeccion.service';
import { useIngresos } from '../../ingresos/hooks/useIngresos';
import { useEgresos } from '../../egresos/hooks/useEgresos';
import { useInversiones } from '../../inversiones/hooks/useInversiones';

export const usePatrimonio = () => {
  
  const { ingresos } = useIngresos();
  const { egresos } = useEgresos();
  const { inversiones } = useInversiones();
  
  const patrimonioService = useMemo(() => new PatrimonioService(), []);
  const proyeccionService = useMemo(() => new ProyeccionService(), []);

  // Calcular patrimonio actual
  const patrimonioActual = useMemo(() => {
    return patrimonioService.calculatePatrimonio(ingresos, egresos, inversiones);
  }, [ingresos, egresos, inversiones, patrimonioService]);

  // Calcular patrimonio por mes
  const patrimonioPorMes = useMemo(() => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    return patrimonioService.calculatePatrimonioByMonth(year, month, ingresos, egresos, inversiones);
  }, [ingresos, egresos, inversiones, patrimonioService]);

  // Calcular patrimonio por año
  const patrimonioPorAño = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return patrimonioService.calculatePatrimonioByYear(currentYear, ingresos, egresos, inversiones);
  }, [ingresos, egresos, inversiones, patrimonioService]);

  // Calcular historial de patrimonio
  const historialPatrimonio = useMemo(() => {
    return patrimonioService.getPatrimonioHistory(ingresos, egresos, inversiones, 12);
  }, [ingresos, egresos, inversiones, patrimonioService]);

  // Calcular balance por mes
  const balancePorMes = useMemo(() => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    return patrimonioService.getBalanceByMonth(year, month, ingresos, egresos);
  }, [ingresos, egresos]);

  // Calcular desglose por categoría
  const desglosePorCategoria = useMemo(() => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    return patrimonioService.getCategoryBreakdown(egresos, year, month);
  }, [egresos]);

  // Calcular proyección conservadora
  const proyeccionConservadora = useMemo(() => {
    return proyeccionService.calculateProyeccionConservadora(
      patrimonioActual.patrimonioNeto,
      10,
      0
    );
  }, [patrimonioActual.patrimonioNeto]);

  // Calcular proyección moderada
  const proyeccionModerada = useMemo(() => {
    return proyeccionService.calculateProyeccionModerada(
      patrimonioActual.patrimonioNeto,
      10,
      0
    );
  }, [patrimonioActual.patrimonioNeto]);

  // Calcular proyección agresiva
  const proyeccionAgresiva = useMemo(() => {
    return proyeccionService.calculateProyeccionAgresiva(
      patrimonioActual.patrimonioNeto,
      10,
      0
    );
  }, [patrimonioActual.patrimonioNeto]);

  // Calcular proyección personalizada
  const calculateProyeccionPersonalizada = (
    montoInicial: number,
    tasaInteres: number,
    años: number,
    aporteMensual: number = 0
  ): Proyeccion => {
    return proyeccionService.calculateProyeccionPersonalizada(
      montoInicial,
      tasaInteres,
      años,
      aporteMensual
    );
  };

  // Calcular proyección comparativa
  const proyeccionComparativa = useMemo(() => {
    return proyeccionService.getProyeccionComparativa(
      patrimonioActual.patrimonioNeto,
      10,
      0
    );
  }, [patrimonioActual.patrimonioNeto]);

  // Calcular tiempo para meta
  const calcularTiempoParaMeta = (
    meta: number,
    tasaInteres: number,
    aporteMensual: number = 0
  ): number => {
    return proyeccionService.calculateTiempoParaMeta(
      patrimonioActual.patrimonioNeto,
      meta,
      tasaInteres,
      aporteMensual
    );
  };

  // Calcular aporte necesario
  const calcularAporteNecesario = (
    meta: number,
    tasaInteres: number,
    años: number
  ): number => {
    return proyeccionService.calculateAporteNecesario(
      patrimonioActual.patrimonioNeto,
      meta,
      tasaInteres,
      años
    );
  };

  // Obtener datos para gráficos
  const getDatosParaGraficos = () => {
    return {
      historialPatrimonio,
      balancePorMes,
      desglosePorCategoria,
      proyeccionComparativa
    };
  };

  return {
    patrimonioActual,
    patrimonioPorMes,
    patrimonioPorAño,
    historialPatrimonio,
    balancePorMes,
    desglosePorCategoria,
    proyeccionConservadora,
    proyeccionModerada,
    proyeccionAgresiva,
    proyeccionComparativa,
    calculateProyeccionPersonalizada,
    calcularTiempoParaMeta,
    calcularAporteNecesario,
    getDatosParaGraficos,
    loading: false,
    error: null
  };
};
